import FieldType from "@/utils/constants/FieldType.ts";
import FieldTypesUtils from "@/utils/common/FieldTypesUtils.ts";
import PlaceUtils from "@/utils/common/PlaceUtils.ts";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import DashboardUtils from "@/utils/common/DashboardUtils.ts";
import Translator from "@/utils/common/Translator.ts";
import router from "@/router/index.ts";
import RouteNames from "@/router/RouteNames.ts";
import type {
  BIDrillDownByClick,
  BIDrillDownByData,
  BIDrillDownByDimension,
  BIDrillDownClick,
  BIDrillDownFilterMetadata,
  BIDrillDownPointValues,
  BIDrillDownReferenceValue,
  BIDrillDownTarget,
  BILinkTarget,
} from "@/components/bi/biChartTypes.ts";

export function isReferenceValue(val: BIDrillDownReferenceValue | string | undefined): val is BIDrillDownReferenceValue {
  return !!val && typeof val === 'object' && typeof (val as BIDrillDownReferenceValue).id === 'string';
}

export function serializeDrillDownFilterValue(
  metadata: BIDrillDownFilterMetadata,
  raw: BIDrillDownReferenceValue | string | undefined,
): string {
  if (raw === undefined || raw === null) return '';
  const paramType = metadata.paramType;
  if ((paramType === FieldType.Reference || paramType === FieldType.Genericreference) && isReferenceValue(raw)) {
    return FieldTypesUtils.toString(paramType, {
      id: raw.id,
      entityType: raw.entityType ?? '',
      code: raw.code ?? '',
      actualCode: '',
      name1: raw.name1 ?? '',
      name2: raw.name2 ?? '',
    });
  }
  if (typeof raw === 'string') return raw;
  return '';
}

export function buildDrillDownFilters(
  target: BIDrillDownTarget,
  pointValues: BIDrillDownPointValues,
  filterMetadata: Record<string, BIDrillDownFilterMetadata>,
  sourceChart?: string,
): Array<{id: string; value: string; sourceChart?: string}> {
  const result: Array<{id: string; value: string; sourceChart?: string}> = [];
  for (const code of target.filterCodes ?? []) {
    const metadata = filterMetadata[code];
    if (!metadata) continue;
    if (!(code in pointValues)) continue;
    const raw = pointValues[code];
    const serialized = serializeDrillDownFilterValue(metadata, raw);
    result.push({id: code, value: serialized, sourceChart});
  }
  return result;
}

// Builds a drill-down click payload for emitting to the dashboard. Returns undefined when the
// click should be skipped (missing source widget code or no filters resolved from the point).
export function buildDrillDownClick(
  target: BIDrillDownTarget,
  pointValues: BIDrillDownPointValues,
  filterMetadata: Record<string, BIDrillDownFilterMetadata>,
  sourceWidgetCode: string | undefined,
  sourceChart: string | undefined,
  sourceLabel?: string,
): BIDrillDownClick | undefined {
  if (!sourceWidgetCode) return undefined;
  const drillDownFilters = buildDrillDownFilters(target, pointValues, filterMetadata, sourceChart);
  if (!drillDownFilters.length) return undefined;
  return {target, sourceWidgetCode, drillDownFilters, filterMetadata, sourceLabel};
}

// Builds a drill-down-by click payload. Returns undefined when sourceWidgetCode is missing.
export function buildDrillDownByClick(
  dimension: BIDrillDownByDimension,
  drillByData: BIDrillDownByData | undefined,
  rowIdx: number | undefined,
  sourceWidgetCode: string | undefined,
  sourceWidgetId: string | undefined,
  dashboardId: string | undefined,
): BIDrillDownByClick | undefined {
  if (!sourceWidgetCode) return undefined;
  const currentDimensionValue = drillByData?.points && rowIdx != null ? drillByData.points[rowIdx] : undefined;
  const dimensionLabel = Translator.isArabic()
    ? (dimension.arTitle || dimension.enTitle || dimension.fieldId)
    : (dimension.enTitle || dimension.arTitle || dimension.fieldId);
  return {
    targetDimensionFieldId: dimension.fieldId,
    dimensionLabel,
    sourceWidgetCode,
    sourceWidgetId: sourceWidgetId ?? '',
    dashboardId,
    currentDimensionFieldId: drillByData?.currentDimensionFieldId ?? '',
    currentDimensionValue,
    currentDimensionParamType: drillByData?.currentDimensionParamType ?? 'Text',
    currentDimensionReferencedEntityType: drillByData?.currentDimensionReferencedEntityType,
  };
}

export interface BILinkPointValue {
  url?: string;
  entityType?: string;
  entityId?: string;
  label?: string;
}

// Shared navigation for clicked link targets. Mirrors DashboardUtils.handleChartClick semantics:
// - Direct URL: external → new tab; relative/internal → goToInternalURL
// - Entity navigation: popup → QuickEditReferenceDialog; newTab → window.open; else → router.push
export function executeLink(linkTarget: BILinkTarget, pointValue: BILinkPointValue): void {
  if (linkTarget.isDirectLink) {
    const url = pointValue.url;
    if (!url) return;
    if (url.startsWith('http://') || url.startsWith('https://'))
      window.open(url, '_blank');
    else
      GUIUtilities.goToInternalURL(url);
    return;
  }
  const editPlace = PlaceUtils.editPlace(pointValue.entityType, pointValue.entityId);
  if (!editPlace) return;
  if (linkTarget.openMode === 'popup')
    DashboardUtils.openQuickEditReferenceDialog(editPlace);
  else if (linkTarget.openMode === 'newTab')
    window.open(PlaceUtils.linkToPlace(editPlace), '_blank');
  else
    router.push({name: RouteNames.EDIT_VIEW_ROUTE, query: editPlace.fetchQuery()});
}
